import useSWR from "swr";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Scale, AlertTriangle, ArrowRight, Trash2, MapPin, Route, CheckCircle2, Circle, CircleDot } from "lucide-react";
import { fetcher } from "@/lib/api";
import "@/i18n/fase9";

// Traffic-light semaphore per task status.
function SemaphoreDot({ status }) {
  const map = {
    ok:        { color: "bg-salnes-emerald",  label: "OK" },
    ok_empty:  { color: "bg-[#F4A261]",       label: "Sen datos" },
    failed:    { color: "bg-salnes-coral",    label: "Erro" },
    default:   { color: "bg-salnes-stone/50", label: "—" },
  };
  const m = map[status] || map.default;
  return <span className={`inline-block w-2 h-2 rounded-full ${m.color}`} title={m.label} />;
}

const TASK_ROWS = [
  { key: "col_points",     icon: MapPin,         labelGl: "Puntos",       labelEs: "Puntos" },
  { key: "containers",     icon: Trash2,         labelGl: "Contedores",   labelEs: "Contenedores" },
  { key: "routes",         icon: Route,          labelGl: "Rutas",        labelEs: "Rutas" },
  { key: "weighins",       icon: Scale,          labelGl: "Pesaxes hoxe", labelEs: "Pesajes hoy" },
  { key: "alarms",         icon: AlertTriangle,  labelGl: "Alarmas",      labelEs: "Alarmas" },
];

export default function SigeusStatusWidget() {
  const { t, i18n } = useTranslation();
  const isGl = (i18n.language || "gl").startsWith("gl");
  const { data: kpi } = useSWR("/sigeus/kpis", fetcher, { refreshInterval: 60000 });
  const { data: syncStatus } = useSWR("/sigeus/sync/status", fetcher, { refreshInterval: 90000 });

  if (!kpi) {
    return (
      <div className="rounded-2xl border hairline bg-white p-6 min-h-[220px] animate-pulse" data-testid="sigeus-widget-skeleton">
        <div className="h-4 w-40 bg-salnes-sand/60 rounded" />
      </div>
    );
  }

  const tasks = syncStatus?.tasks || {};
  const lastRun = Object.values(tasks).map((t) => t?.last_run_at).filter(Boolean).sort().slice(-1)[0];

  const values = {
    col_points: kpi.col_points_active,
    containers: kpi.containers_total,
    routes: syncStatus?.tasks?.routes?.records ?? "—",
    weighins: kpi.weighins_today?.count ?? 0,
    alarms: kpi.alarms_open ?? 0,
  };

  return (
    <Link
      to="/app/contedores"
      className="rounded-2xl border hairline bg-white p-6 block hover:shadow-lg transition-all group"
      data-testid="sigeus-status-widget"
    >
      <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.22em] text-salnes-atlantic">
        <Trash2 size={14} strokeWidth={1.6} /> siGEUS
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-4">
        {TASK_ROWS.map((row) => {
          const status = tasks[row.key]?.status;
          const records = tasks[row.key]?.records;
          const Icon = row.icon;
          return (
            <div
              key={row.key}
              className="flex items-center gap-2 min-w-0"
              data-testid={`sigeus-widget-task-${row.key}`}
            >
              <SemaphoreDot status={status} />
              <Icon size={12} strokeWidth={1.6} className="text-salnes-stone shrink-0" />
              <span className="text-[10px] uppercase tracking-[0.14em] text-salnes-stone truncate">
                {isGl ? row.labelGl : row.labelEs}
              </span>
              <span className="ml-auto font-mono text-xs text-salnes-ink tabular-nums">
                {typeof values[row.key] === "number" ? values[row.key].toLocaleString() : values[row.key]}
              </span>
            </div>
          );
        })}
      </div>

      {tasks.weighins?.status === "ok_empty" && (
        <div
          className="mt-2 rounded-lg bg-[#F4A261]/10 text-[#8B4513] px-3 py-1.5 text-[10px]"
          data-testid="sigeus-widget-weighins-note"
        >
          {isGl
            ? "Pesaxes: sen datos aínda (upstream siGEUS baleiro)"
            : "Pesajes: sin datos aún (upstream siGEUS vacío)"}
        </div>
      )}

      {lastRun && (
        <div className="mt-3 text-[10px] text-salnes-stone" data-testid="sigeus-widget-last-run">
          {isGl ? "Última sincronización: " : "Última sincronización: "}
          <span className="font-mono text-salnes-ink">{String(lastRun).slice(0, 19).replace("T", " ")}</span>
        </div>
      )}

      <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-salnes-atlantic group-hover:gap-2 transition-all">
        {t("fase9.contedores_widget.weighins_link")}
        <ArrowRight size={12} />
      </div>
    </Link>
  );
}
